<template>
    <div class="ur-autocomplete">
        <ur-text-field class="ur-autocomplete__text-field"
                       v-model="inputValue"
                       v-on="$listeners"
                       @keydown="onKeyDown"
                       @keydown.up.prevent="moveSelectedUp"
                       @keydown.down.prevent="moveSelectedDown"
                       @keydown.enter="selectItem"
                       @focus="listOpened=true"
                       :placeholder="placeholder"
                       :guide-message="guideMessage"
                       :success-message="successMessage"
                       :shape="shape"
                       :border="border"
                       :underline="underline"
                       :message-style="messageStyle"
                       :disabled="disabled"
                       :counter="counter"
                       :size="size"
                       :large="large"
                       :medium="medium"
                       :small="small"
                       :tabindex="tabindex"
                       :lg="lg"
                       :md="md"
                       :sm="sm"
                       :bor="bor"
                       :ul="ul"
                       :rules="rules"
                       :lazy="lazy"
                       :no-shake="noShake"
        />
        <div :class="listClass" ref="floating" v-ur-attach-root v-click-outside.anchor="()=>{listOpened=false}">
            <template v-for="(item, idx) in dItemList" >
                <div :key="idx"
                     :class="itemClass(idx)"
                     @click.stop="selectItem(idx)">{{item}}</div>
            </template>
        </div>
    </div>
</template>

<script>
    import urValidatorMixin from '../../../mixins/urValidator/urValidatorMixin.vue';
    export default {
        name: "ur-autocomplete",
        mixins: [urValidatorMixin],
        props: {
            // $ver: v0.2.1
            // $v-model
            // v-model로 바인딩되는 값으로, 텍스트필드의 값과 연계됩니다.
            // #The value bound to v-model, associated with the value of the textfield.
            // $tags IDE, value
            value : [String,Number],
            // $ver: v0.2.1
            // 표시될 아이템 자동완성 목록입니다. 정적 데이터를 이용할 때 주로 사용됩니다.
            // #This is a list of auto-complete items to be displayed. Mainly used when using static data.
            // $tags IDE
            itemList : {type:Array},
            // $ver: v0.2.1
            // 목록 필터 로직을 부여합니다. item-list와 함께 사용됩니다.
            // #Gives list filter logic. Used with item-list.
            // $tags IDE
            itemFilter : {type: Function},
            // $ver: v0.2.1
            // 자동완성 목록의 최대 갯수입니다.
            // #The maximum number of autocomplete lists.
            // $tags IDE, value
            itemMax : {type:Number, default:10},
            // $ver: v0.2.1
            // throttle 에 지정할 시간입니다. text-changed 이벤트를 등록할 경우, 값 변경시 지정된 시간에 한번씩만 이벤트를 발생 시킵니다. 단위는 ms입니다.
            // #Time to specify for throttle. When registering a text-changed event, the event occurs only once at a specified time when the value is changed. The unit is ms.
            // $tags IDE, value
            throttleTime : {type:Number, default:200},
            // $ver: v0.2.1
            // debounce 에 지정할 시간입니다. text-changed 이벤트를 등록할 경우, 값 변경시 동작이 완료되고 지정된 시간 이후에 이벤트를 발생 시킵니다. 단위는 ms입니다.
            // #Time to debounce. When registering a text-changed event, the operation is completed when the value is changed and an event occurs after the specified time. The unit is ms.
            // $tags IDE, value
            debounceTime : {type:Number, default:200},

            // $ver: v0.2.1
            // 텍스트 필드가 빈 값일 때 표시할 문자열 입니다.
            // #The string to display when the text field is empty.
            // $tags IDE, placeholder
            placeholder: {type: String},

            // $ver: v0.2.1
            // Guide 메시지로 표시할 문자열 입니다.
            // #The string to display as the guide message.
            guideMessage: {type: String, default: undefined},

            // $ver: v0.2.1
            // Success 메시지로 표시할 문자열 입니다.
            // #The string to display as a Success message.
            successMessage: {type: String, default: undefined},

            // $ver: v0.2.1
            // 모양입니다. 'border','underline','none' 중 하나를 입력해야 합니다.
            // #The shape. You must enter one of three types: border, underline, none
            // $default: border
            // $tags IDE, shape
            shape: {
                validator: function (value) {
                    // The value must match one of these strings
                    return ['border', 'underline','none'].indexOf(value) !== -1
                }
                // type: String,
                // default: 'border'
            },

            // $ver: v0.2.1
            // 컴포넌트를 Border 형태로 표현합니다.
            // #Represents a component in the form of a Border.
            // $default: false
            border: {type: Boolean, default: false},

            // $ver: v0.2.1
            // 컴포넌트를 Underline 형태로 표현합니다.
            // #Represents a component in underline form.
            // $default: false
            underline: {type: Boolean, default: false},

            // $ver: v0.2.1
            // 가이드, 성공, 안내메시지 표현 형태 속성입니다. 'heavy', 'light' 속성을 지정할 수 있습니다.
            // #Guide, Success, Announcement appearance attributes. You can specify the 'heavy' and 'light' attributes.
            messageStyle: {
                type: String,
                validator: function(value) {
                    return ['heavy', 'light'].indexOf(value) !== -1;
                },
                default: 'heavy'
            },
            
            // $ver: v0.2.1
            // 비활성화 상태로 전환합니다.
            // #Switch to inactive state.
            // $default: false
            // $tags IDE, disabled
            disabled: {type: Boolean, default: false},


            // $ver: v0.2.1
            // 입력될 문자열의 최대 길이 값을 지정합니다. urValidator 연계로 실제 값 유효성 검사를 수행해야 합니다. 이 속성이 입력되면 우측 하단에 입력된 문자열의 길이를 표시합니다.
            // #Specifies the maximum length value of the string to be entered. You must perform actual value validation with the urValidator linkage.
            // #When this property is entered, it displays the length of the entered text in the lower right corner.
            // $tags IDE, counter
            counter: {type: [String, Number], default: undefined},

            // $ver: v0.2.1
            // 크기 입니다. 'large','medium','small' 두 가지 중 하나를 입력해야 합니다.
            // #The size You must enter one of three types: large, medium, small.
            // $default: medium
            // $tags IDE, size
            size: {
                validator: function (value) {
                    // The value must match one of these strings
                    return ['large','medium', 'small'].indexOf(value) !== -1
                },
                // type: String,
                //default: 'medium'
            },

            // $ver: v0.2.1
            // 컴포넌트를 Large 크기로 조정합니다.
            // #Resize the component to large size.
            // $default: false
            large: {type: Boolean, default: false},

            // $ver: v0.2.1
            // 컴포넌트를 Medium 크기로 조정합니다.
            // #Resize the component to medium size.
            // $default: false
            medium: {type: Boolean, default: false},

            // $ver: v0.2.1
            // 컴포넌트를 Small 크기로 조정합니다.
            // #Resize the component to Small.
            // $default: false
            small: {type: Boolean, default: false},

            // $ver: v0.1.2
            // tabindex를 설정합니다.
            // #Set tabindex.
            tabindex: {type:[Number,String]},

            // $ver: v0.2.1
            // large 속성의 약어입니다.
            // $abbr: large
            lg: {type: Boolean, default: false}, // large

            // $ver: v0.2.1
            // medium 속성의 약어입니다.
            // $abbr: medium
            md: {type: Boolean, default: false}, // medium

            // $ver: v0.2.1
            // small 속성의 약어입니다.
            // $abbr: small
            sm: {type: Boolean, default: false}, // small

            // $ver: v0.2.1
            // border 속성의 약어입니다.
            // $abbr: border
            bor: {type: Boolean, default: false}, // border

            // $ver: v0.2.1
            // underline 속성의 약어입니다.
            // $abbr: underline
            ul: {type: Boolean, default: false}, // underline
        },
        data() {
            return {
                inputValue: '',
                displayedValue: '',
                _attrs: null,
                dItemList: [],
                throttleHandler: null,
                debounceHandler: null,
                selectedIdx: 1,
                listOpened: true,
            };
        },
        mounted(){
            window.addEventListener('resize', this.resize);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.resize);
        },
        computed: {
            cItemMax() {
                let rtn = this.itemMax * 1;
                return isNaN(rtn)? 10 : rtn;
            },
            cThrottleTime() {
                let rtn = this.throttleTime * 1;
                return isNaN(rtn)? 10 : rtn;
            },
            cDebounceTime() {
                let rtn = this.debounceTime * 1;
                return isNaN(rtn)? 10 : rtn;
            },
            cSize() {
                if(this.size === 'large' || this.size === 'small' || this.size === 'medium') {
                    return this.size;
                }

                if(this.large || this.lg) {return 'large'}
                if(this.medium || this.md) {return 'medium'}
                if(this.small || this.sm) {return 'small'}
                return 'medium';
            },
            isListShow() {
                return this.listOpened && this.inputValue !== '' && this.dItemList.length > 0;
            },
            listClass() {
                let rtn = [];
                rtn.push('ur-autocomplete__items');
                return rtn;
            },
            itemClass() {
                return (idx) => {
                    let rtn = ['ur-autocomplete__item'];
                    if(this.isSelected(idx)){
                        rtn.push('ur-autocomplete__selected');
                    }
                    rtn.push('ur-autocomplete__item-' + this.cSize);
                    return rtn;
                }
            },
        },
        created() {
        },
        watch : {
            isListShow(v,w) {
                if(v) {
                    this.$refs.floating.open();
                } else {
                    this.$refs.floating.close();
                }
            },
            itemList : {
                deep: true,
                handler(newVal, oldVal) {
                    this.dItemList = newVal.slice(0, this.cItemMax);
                    this.selectedIdx = 0;
                }
            },
            inputValue(newVal, oldVal) {
                let rtn = [];
                if(!newVal) {
                    this.dItemList = rtn;
                    return;
                }
                this.listOpened = true;
                if (this.$listeners['text-changed']) {
                    // text-changed 이벤트 있을 경우
                    //debounce
                    clearTimeout(this.debounceHandler);
                    this.debounceHandler = setTimeout(() => {
                        // $ver: v0.2.1
                        // $arg: updated value
                        // 입력창의 값이 변할 때, 값에 따른 결과를 리스트로 표시하기 위한 로직을 받는 이벤트입니다.
                        // #This event receives the logic to display the result of the value as a list when the value of the input window changes.
                        this.$emit('text-changed', newVal);
                    }, this.cDebounceTime);

                    //throttle
                    if(!this.throttleHnadler){
                        this.throttleHnadler = setTimeout(() => {
                            this.throttleHnadler = null;
                            // $ver: v0.2.1
                            // $arg: text
                            // 입력창의 값이 변할 때, 값에 따른 결과를 리스트로 표시하기 위한 로직을 받는 이벤트입니다.
                            // #This event receives the logic to display the result of the value as a list when the value of the input window changes.
                            this.$emit('text-changed', newVal);
                        }, this.cThrottleTime);
                    }
                } else {
                    //item text-changed event 가 없고 list가 있을 경우 item list 를 가지고 수행
                    let count = 0;
                    if (this.itemFilter && typeof this.itemFilter === 'function') {
                        //item-filter event가 등록되어 있으면 그걸로 포함여부를 수행
                        for(let i = 0 ; i < this.itemList.length  && count < this.cItemMax; i ++) {
                            let item = this.itemList[i];
                            if(this.itemFilter(item, newVal)) {
                                rtn.push(item);
                                count++;
                            }
                        }
                    } else {
                        //없으면 startsWith로 포함
                        for(let i = 0 ; i < this.itemList.length  && count < this.cItemMax; i ++) {
                            let item = this.itemList[i];
                            if(item.startsWith(newVal)) {
                                rtn.push(item);
                                count++;
                            }
                        }
                    }
                    this.dItemList = rtn;
                    this.selectedIdx = 0;
                }
            }
        },
        methods : {
            isSelected(idx) {
                return this.selectedIdx === idx;
            },
            onKeyDown(e) {
                // if (e.key === 'ArrowDown') {
                    
                // } else if (e.key === '') {
                    
                // }
            },
            moveSelectedUp(e) {
                if(this.selectedIdx > 0){
                    this.selectedIdx--;
                } else if (this.selectedIdx === 0) {
                    this.selectedIdx = this.dItemList.length - 1;
                }
            },
            moveSelectedDown(e) {
                if(this.selectedIdx < this.dItemList.length - 1){
                    this.selectedIdx++;
                }else if (this.selectedIdx === this.dItemList.length - 1){
                    this.selectedIdx = 0;
                }
            },
            selectItem(idx) {
                if(!this.isListShow) {
                    return;
                }
                if(typeof idx === 'number' && idx > -1){
                    this.selectedIdx = idx;
                }
                this.inputValue = this.dItemList[this.selectedIdx];
                this.$emit('input', this.dItemList[this.selectedIdx]);
                this.listOpened = false;
            },
            resize(e){
                if(this.$refs.floating.isShow() === true){
                    this.$refs.floating.resize(e);
                }
            },
        },
        directives: {
            'autocomplete-click-outside': {
                bind: function(el, binding) {
                    // Define Handler and cache it on the element
                    const handler = (e) => {
                        if (!(el.contains(e.target) || el === e.target)) {
                            binding.value(e)
                        }
                    }
                    el.__vueClickOutside__ = handler
                    // add Event Listeners
                    document.addEventListener('click', handler)
                },
                unbind: function(el, binding) {
                    // Remove Event Listeners
                    document.removeEventListener('click', el.__vueClickOutside__)
                    el.__vueClickOutside__ = null
                }
            }
        }
    }
</script>

